import {
  AfterContentChecked,
  AfterContentInit,
  ChangeDetectorRef,
  Component,
  DoCheck,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { CurrencyPipe, DecimalPipe } from '@angular/common';
import { animate, state, style, transition, trigger } from '@angular/animations';

import { NzModalService } from 'ng-zorro-antd/modal';
import {
  ErrorsHandlingService,
  AuthService,
  ViewerRestrictionService
} from '../../../../core/services';
import {
  AppOptions,
  HelpersInterface,
} from '../../../../core/interfaces';
import { CompanyInterface, PartnerInterfaceGlobal } from '../../../../core/models';
import { ConnectionTypeEnum, UserRole } from '../../../../core/enum';

import { EditDspDialogComponent } from '../../modals';
import { MainDashboardInterface, sortableDSPTableColumns } from '../../models';
import { MainService, MainTableService } from '../../services';
import { AppConfig } from '../../../../core/configs';

const slideInOut = trigger('slideInOut', [
  state('void', style({ height: '0px' })),
  state('*', style({ height: '*' })),
  transition('void => *', animate('500ms ease-in')),
  transition('* => void', animate('500ms ease-out'))
]);

const toggleInOut = trigger('toggleInOut', [
  state('void', style({ height: '0px' })),
  state('*', style({ height: '*' })),
  transition('* => void', animate('300ms ease-in')),
  transition('void => *', animate('300ms ease-out')),
]);

const toggleArrow = trigger('toggleArrow', [
  state('none', style({ transform: 'rotate(0)' })),
  state('visible', style({ transform: 'rotate(90deg)' })),
  transition('visible => none', animate('300ms ease-in')),
  transition('none => visible', animate('300ms ease-out')),
]);

@Component({
  selector: 'app-main-table-dsp',
  templateUrl: './main-table-dsp.component.html',
  styleUrls: ['./main-table-dsp.component.scss'],
  animations: [slideInOut, toggleInOut, toggleArrow],
  standalone: false
})
export class MainTableDspComponent implements OnInit, AfterContentInit, AfterContentChecked, DoCheck {

  @Input() public companies: CompanyInterface.ICompany[];
  @Input() public dataCompanies: MainDashboardInterface.ICompaniesDataForMainTable[];
  @Input() public dataEndpoints: MainDashboardInterface.IEndpointsDataForMainTable[];
  @Input() public paginationState: {
    current_page: number;
    per_page: number;
    total: number;
  };
  @Input() public demand: boolean;
  @Input() public osList: HelpersInterface.SelectItemHelper<string | number, string>[];
  @Input() public options: AppOptions;
  @Input() public type: string;
  @Input() public filter: string;
  @Input() public activity: number | string;
  @Input() public showEndpoint: boolean;
  @Input() public userRole: UserRole;
  @Input() public allCompanies: CompanyInterface.ICompany[];
  @Input() public isSorting: boolean;

  @Input() public set partnerSettings(value: PartnerInterfaceGlobal.IPartnerSettings | PartnerInterfaceGlobal.IPartnerGeneral) {
    this._partnerSettings = value;
  }

  public get partnerSettings(): PartnerInterfaceGlobal.IPartnerSettings | PartnerInterfaceGlobal.IPartnerGeneral {
    return this._partnerSettings;
  }

  @Input() public rtbVersions: HelpersInterface.SelectItemHelper<string, string>[];
  @Output() public sortTable = new EventEmitter<{ sortBy: string; sortOrder: string; }>();
  @Output() public heightTable = new EventEmitter();
  @Output() public changePage = new EventEmitter();
  @Output() public updateDSP = new EventEmitter<string>();
  @Output() public duplicatedEndpoint = new EventEmitter<{ endpointId: any; side: string; }>();
  @Output() public deleteEndpoint = new EventEmitter<{ endpointId: any; side: string; companyId?: number }>();
  @Output() public activateCompany = new EventEmitter<{ side: string }>();
  @Output() public updateEndpointActiveStatus = new EventEmitter<{ side: string; type: string }>();

  get filterLists() {
    return this._filterLists;
  }

  @Input() set filterLists(value: any) {
    this._filterLists = value;
  }

  @Input() public set dspSpinnerStatus(value: boolean) {
    this._dspSpinnerStatus = value;
  }

  public get dspSpinnerStatus(): boolean {
    return this._dspSpinnerStatus;
  }

  @Input() public set schaninNodeLimitItems(value: { id: number; name: string }[]) {
    this._schaninNodeLimitItems = value;
  }

  public get schaninNodeLimitItems(): { id: number; name: string }[] {
    return this._schaninNodeLimitItems;
  }

  @Input() public set statesList(value: HelpersInterface.SelectItemHelper<string, string>[]) {
    this._statesList = value;
  }

  public get statesList(): HelpersInterface.SelectItemHelper<string, string>[] {
    return this._statesList;
  }

  protected tableHeight = 'auto';
  protected placeholderMarginTop = 'auto';
  protected companiesState: { [key: string]: boolean } = {};
  protected connectionTypes = ConnectionTypeEnum;

  private _partnerSettings: PartnerInterfaceGlobal.IPartnerSettings | PartnerInterfaceGlobal.IPartnerGeneral;

  private tbodyHeight: number;
  private sortableColumns: string[] = sortableDSPTableColumns;

  private _dspSpinnerStatus = true;
  private _schaninNodeLimitItems: { id: number; name: string }[] = [];
  private _filterLists: HelpersInterface.IFilterListsItems[];
  private _statesList: HelpersInterface.SelectItemHelper<string, string>[] = [];

  private panelIsCollapsed = false;

  constructor(
    protected currencyPipe: CurrencyPipe,
    protected decimalPipe: DecimalPipe,
    protected mainTableService: MainTableService,
    private cd: ChangeDetectorRef,
    private modalService: NzModalService,
    private errorsHandlingService: ErrorsHandlingService,
    private mainService: MainService,
    private authService: AuthService,
    private viewerRestrictionService: ViewerRestrictionService,
    private initConfig: AppConfig
  ) { }

  public ngOnInit(): void {
    this.initConfig.isCollapsed$.subscribe((value) => {
      this.panelIsCollapsed = value;
    });
  }

  public ngDoCheck(): void {
    const element = document.getElementById('ssp-spinner');
    if (element) {
      const rect = element.getBoundingClientRect();
      this.placeholderMarginTop = rect.top / 2 + 75 + 'px';
    }

    if (!this.panelIsCollapsed && window.innerWidth < 1367) {
      this.tableHeight = window.innerHeight - 560 + 'px';
    } else {
      this.tableHeight = window.innerHeight - 500 + 'px';
    }
  }

  public ngAfterContentChecked() {
    this.cd.detectChanges();
  }

  public ngAfterContentInit() {
    this.tableHeight = window.innerHeight - 500 + 'px';
    this.tbodyHeight = window.innerHeight - 209;
    window.addEventListener('resize', () => {
      this.tableHeight = window.innerHeight - 500 + 'px';
      this.tbodyHeight = window.innerHeight - 209;
    });
    this.heightTable.emit(this.tbodyHeight);
  }

  protected get colSpanNumbers(): number {
    return this.options.dsp.filter(option => option.active).length + 4;
  }

  protected isDisabledSwitch(endpoint: MainDashboardInterface.IEndpointsDataForMainTable): boolean {
    const restrictionConnectionTypes = [4, 5];
    if (this.partnerSettings?.partner_plan === 3 && restrictionConnectionTypes.includes(endpoint.connectionType)) {
      return true;
    } else {
      return this.userRole === UserRole.viewer || !!endpoint.deletedAt;
    }
  }

  protected getMargin(endpoint: MainDashboardInterface.IEndpointsDataForMainTable): number | string | void {
    return this.mainTableService.getMarginByTypeHandler(endpoint);
  }

  protected isOptionActive(attr: string, side: string): boolean {
    return this.mainTableService.isOptionActiveHandler(attr, side, this.options);
  }

  protected isClosed(i: number): 'none' | 'visible' {
    return this.mainTableService.isClosedCompanyHandler(i, this.companiesState);
  }

  protected sort(sortBy: string) {
    const sortState = this.mainTableService.onSortDSPTable(sortBy, this.sortableColumns);

    if (sortState) {
      this.sortTable.emit(sortState);
    }
  }

  protected toggleCompanyState(i: number) {
    this.companiesState[i] = !this.companiesState[i];
  }

  protected openDspEditModal(endpointId: number, companyId: number) {
    const partnerSettings = this.authService.partner;
    const dialogRef = this.modalService.create({
      nzContent: EditDspDialogComponent,
      nzMaskClosable: false,
      nzClassName: 'endpoint-edit-dsp-modal',
      nzWrapClassName: 'endpoint-edit-dsp-modal-wrap',
      nzStyle: { top: '100px', width: '850px', overflow: 'hidden', },
      nzBodyStyle: { width: '850px', padding: '0', height: '580px', overflowY: 'scroll' },
      nzClosable: false,
      nzFooter: null,
      nzTitle: '',
      nzData: {
        endpointId: endpointId,
        companyId: companyId,
        partnerSettings: partnerSettings,
        companies: this.allCompanies,
        userRole: this.userRole,
        filterLists: this.filterLists,
        osList: this.osList,
        rtbVersions: this.rtbVersions,
        schaninNodeLimitItems: this.schaninNodeLimitItems,
        statesList: this.statesList,
      }
    });

    dialogRef.afterClose.subscribe(res => {
      this.updateDSP.emit(res);
    });
  }

  protected onUpdateDSP(data: { status: string; companyId: number; }): void {
    this.companiesState[data.companyId] = !this.companiesState[data.companyId];
    this.updateDSP.emit(data.status);
  }

  protected onEndpointActiveStatusChange(emitData: { side: string; type: string }): void {
    this.updateEndpointActiveStatus.emit(emitData);
  }

  protected onEndpointTableActiveStatusChange(e: number, id: number, side: string) {
    const data = {
      active: Number(e)
    };
    this.mainService.updateEndpointById(id, data, side)
      .subscribe(() => {
        this.updateEndpointActiveStatus.emit({ side: 'dsp', type: 'endpoints' });
      },
        error => {
          this.errorsHandlingService.handleError(error);
        });
  }

  protected onChangeActiveCompany(_: MouseEvent,
    companyId: number,
    type: string,
    company: MainDashboardInterface.ICompaniesDataForMainTable) {
    if (this.authService.userRole === UserRole.viewer) {
      this.viewerRestrictionService.restrictUserViewer();
      return;
    }

    switch (Boolean(company.active)) {
      case true:
        this.mainTableService.onDeactivateCompanyHandler(companyId, type, company, () => this.activateCompany.emit({ side: 'dsp' }));
        break;
      case false:
        this.mainTableService.onActivateCompanyHandler(companyId, type, company, () => this.activateCompany.emit({ side: 'dsp' }));
        break;
    }
  }

  protected onPageIndexChange(pageIndex: number, type: string): void {
    this.paginationState.current_page = pageIndex;
    const data = {
      current_page: this.paginationState.current_page,
      type
    };
    this.changePage.emit(data);
  }

  protected deleteListEndpoint(data: { endpointId: number, side: string, companyId?: number }): void {
    this.deleteEndpoint.emit(data);
  }

  protected duplicateListEndpoint(data: { endpointId: number, side: string }): void {
    this.duplicatedEndpoint.emit(data);
  }
}
